import type {
  EmailAddressResource,
  EnterpriseConnectionResource,
  OrganizationDomainResource,
  UserResource,
} from '@clerk/shared/types';

import type { EnterpriseConnectionProviderType, OidcProviderType } from '../types';

export const isOidcProvider = (provider: string): provider is OidcProviderType =>
  provider.startsWith('oidc_') || provider.startsWith('oauth_custom_');

/**
 * The email whose domain backs the connection: the user's primary address if
 * present, otherwise the first address they have not yet verified (the one they
 * are working through). The single source of this rule — the domain entity, the
 * reachability guards, and the verify-domain step all read it from here, so they
 * can never drift. Pure: a plain function over the user resource, no React.
 *
 * The two branches converge today because FAPI promotes the first verified
 * address to primary; keeping the rule in one place means any future divergence
 * stays consistent across every call site.
 */
export const connectionBackingEmail = (user: UserResource | null | undefined): EmailAddressResource | undefined =>
  user?.primaryEmailAddress ?? user?.emailAddresses?.find(e => e.verification.status !== 'verified');

/** FAPI returns the list unordered; every reader sorts through here so they agree on "the first one". */
export const sortEnterpriseConnections = (
  connections: EnterpriseConnectionResource[],
): EnterpriseConnectionResource[] =>
  [...connections].sort((a, b) => {
    const aCreatedAt = a.createdAt?.getTime();
    const bCreatedAt = b.createdAt?.getTime();

    if (aCreatedAt !== bCreatedAt) {
      if (aCreatedAt === undefined) {
        return 1;
      }
      if (bCreatedAt === undefined) {
        return -1;
      }
      return aCreatedAt - bCreatedAt;
    }

    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });

/**
 * The inputs {@link organizationEnterpriseConnection} composes. Every field is a
 * plain value — the entity is pure and knows nothing about React or the wizard.
 */
export interface OrganizationEnterpriseConnectionInput {
  /** The connection in scope, i.e. the one the wizard is editing. */
  connection: EnterpriseConnectionResource | null | undefined;
  /** Probed upstream — not a property of the connection resource itself. */
  hasSuccessfulTestRun: boolean;
}

/** Display-facing lifecycle summary — the wizard's navigation guards keep reading the raw booleans. */
export type OrganizationEnterpriseConnectionStatus = 'unconfigured' | 'in_progress' | 'active' | 'inactive';

/**
 * The active organization's SSO-config domain entity: an immutable, pure value
 * object the wizard makes every flow decision from. A snapshot of flattened booleans/values.
 */
export interface OrganizationEnterpriseConnection {
  readonly provider: EnterpriseConnectionProviderType | undefined;
  readonly hasConnection: boolean;
  readonly isActive: boolean;
  readonly hasMinimumConfiguration: boolean;
  readonly hasSuccessfulTestRun: boolean;
  readonly status: OrganizationEnterpriseConnectionStatus;
}

export const isEnterpriseConnectionConfigured = (
  connection: EnterpriseConnectionResource | null | undefined,
): boolean => {
  if (!connection) {
    return false;
  }
  if (isOidcProvider(connection.provider)) {
    const oauthConfig = connection.oauthConfig;
    return Boolean(
      oauthConfig?.clientId && (oauthConfig.discoveryUrl || (oauthConfig.authUrl && oauthConfig.tokenUrl)),
    );
  }
  return Boolean(connection.samlConnection?.idpSsoUrl && connection.samlConnection?.idpEntityId);
};

export const isOrganizationDomainVerified = (domain: OrganizationDomainResource): boolean =>
  domain.ownershipVerification?.status === 'verified';

/**
 * Domains every connection other than `scopedConnectionId` already authenticates,
 * keyed to that connection's name. FAPI rejects a domain shared by two
 * connections of the same instance, so the wizard never offers these.
 */
export const domainsClaimedByOtherConnections = (
  connections: EnterpriseConnectionResource[],
  scopedConnectionId: string | undefined,
): Map<string, string> => {
  const claimed = new Map<string, string>();
  for (const connection of connections) {
    if (connection.id === scopedConnectionId) {
      continue;
    }
    for (const domain of connection.domains ?? []) {
      claimed.set(domain, connection.name);
    }
  }
  return claimed;
};

/**
 * The domains a connection would receive before its admin touches the
 * selection: every verified organization domain no other connection claims.
 */
export const defaultConnectionDomains = (
  organizationDomains: OrganizationDomainResource[] | null | undefined,
  claimed: Map<string, string>,
): string[] =>
  (organizationDomains ?? [])
    .filter(domain => isOrganizationDomainVerified(domain) && !claimed.has(domain.name))
    .map(domain => domain.name);

/**
 * Whether the connection's domains let the wizard move past the domains step:
 * at least one domain, none of them still pending verification. A connection
 * domain missing from the organization list was accepted by FAPI already, so it
 * does not block.
 */
export const areConnectionDomainsReady = (
  connectionDomains: readonly string[],
  organizationDomains: OrganizationDomainResource[] | null | undefined,
  claimedDomains?: ReadonlyMap<string, string>,
): boolean =>
  connectionDomains.length > 0 &&
  connectionDomains.every(name => {
    if (claimedDomains?.has(name)) {
      return false;
    }
    const organizationDomain = organizationDomains?.find(domain => domain.name === name);
    return !organizationDomain || isOrganizationDomainVerified(organizationDomain);
  });

const connectionStatus = ({
  hasConnection,
  isActive,
  hasMinimumConfiguration,
  hasSuccessfulTestRun,
}: Pick<
  OrganizationEnterpriseConnection,
  'hasConnection' | 'isActive' | 'hasMinimumConfiguration' | 'hasSuccessfulTestRun'
>): OrganizationEnterpriseConnectionStatus => {
  if (!hasConnection) {
    return 'unconfigured';
  }
  if (isActive) {
    return 'active';
  }
  if (hasMinimumConfiguration && hasSuccessfulTestRun) {
    return 'inactive';
  }
  return 'in_progress';
};

export const organizationEnterpriseConnection = ({
  connection,
  hasSuccessfulTestRun,
}: OrganizationEnterpriseConnectionInput): OrganizationEnterpriseConnection => {
  const hasConnection = Boolean(connection);
  const isActive = Boolean(connection?.active);
  const hasMinimumConfiguration = isEnterpriseConnectionConfigured(connection);

  return {
    provider: connection?.provider as EnterpriseConnectionProviderType | undefined,
    hasConnection,
    isActive,
    hasMinimumConfiguration,
    hasSuccessfulTestRun,
    status: connectionStatus({ hasConnection, isActive, hasMinimumConfiguration, hasSuccessfulTestRun }),
  };
};
